import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import { AutocompleteCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import {
  DEFAULT_TASK_HISTORY_AUDIENCE_OPTION,
  TaskHistoryAudience,
  taskHistoryAudienceOptions,
} from 'constants/tasks';

import { showMessageAction } from 'store/global/actions';
import { postTaskMessageAction } from 'store/loanApplications/actions';

import {
  COMPOSER_AUDIENCE_LABEL,
  COMPOSER_MESSAGE_MAX_LENGTH,
  COMPOSER_MESSAGE_ROWS,
  COMPOSER_PLACEHOLDER,
  COMPOSER_SEND_LABEL,
  MESSAGE_POSTED_SUCCESS,
} from '../../TaskThread.constants';
import {
  composerActionsStyles,
  composerAudienceStyles,
  composerSendButtonStyles,
  composerStyles,
} from '../../TaskThread.styles';
import { TaskThreadTestIds } from '../../TaskThread.testIds';

// Defaults to InternalOnly so an untouched audience picker can never widen visibility.
const TaskThreadComposer = ({ taskId, canChooseAudience = false, onPosted }) => {
  const dispatch = useDispatch();

  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState(DEFAULT_TASK_HISTORY_AUDIENCE_OPTION);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSendDisabled = isSubmitting || !message.trim();

  const onChangeMessage = useCallback(event => setMessage(event.target.value), []);

  const onChangeAudience = useCallback((event, option) => setAudience(option), []);

  const onSend = useCallback(() => {
    if (!message.trim()) {
      return;
    }

    setIsSubmitting(true);

    dispatch(
      postTaskMessageAction({
        taskId,
        message: message.trim(),
        audience: audience?.value ?? TaskHistoryAudience.INTERNAL_ONLY,
      }),
    )
      // catchAsyncActionError re-rejects after toasting, so resolution alone means success —
      // a falsy body (e.g. 204) must not be mistaken for a failure and provoke a duplicate send.
      .then(() => {
        setMessage('');
        setAudience(DEFAULT_TASK_HISTORY_AUDIENCE_OPTION);
        dispatch(showMessageAction({ message: MESSAGE_POSTED_SUCCESS, variant: 'success' }));
        onPosted?.();
      })
      .catch(Error)
      .finally(() => setIsSubmitting(false));
  }, [dispatch, taskId, message, audience, onPosted]);

  return (
    <Box data-testid={TaskThreadTestIds.COMPOSER} sx={composerStyles}>
      <TextFieldCDS
        fullWidth
        multiline
        dataTestId={TaskThreadTestIds.COMPOSER_MESSAGE_INPUT}
        disabled={isSubmitting}
        maxLength={COMPOSER_MESSAGE_MAX_LENGTH}
        name="taskThreadMessage"
        placeholder={COMPOSER_PLACEHOLDER}
        rows={COMPOSER_MESSAGE_ROWS}
        value={message}
        onChange={onChangeMessage}
      />

      <Box sx={composerActionsStyles}>
        {canChooseAudience && (
          <Box sx={composerAudienceStyles}>
            <AutocompleteCDS
              disableClearable
              dataTestId={TaskThreadTestIds.COMPOSER_AUDIENCE_SELECT}
              disabled={isSubmitting}
              label={COMPOSER_AUDIENCE_LABEL}
              name="taskThreadAudience"
              options={taskHistoryAudienceOptions}
              value={audience}
              withRequiredIcon={false}
              onChange={onChangeAudience}
            />
          </Box>
        )}

        <Button
          data-testid={TaskThreadTestIds.COMPOSER_SEND_BUTTON}
          disabled={isSendDisabled}
          sx={composerSendButtonStyles}
          variant="contained"
          onClick={onSend}
        >
          {COMPOSER_SEND_LABEL}
        </Button>
      </Box>
    </Box>
  );
};

TaskThreadComposer.propTypes = {
  taskId: PropTypes.string.isRequired,
  canChooseAudience: PropTypes.bool,
  onPosted: PropTypes.func,
};

export default TaskThreadComposer;
